import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Zoom, Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/zoom";

interface ImageCarouselProps {
  images: string[];
  height?: number;
  /** 自动播放间隔（ms），默认 3000；传 0 关闭 */
  autoplayDelay?: number;
  /** 图片名（用于 alt / a11y） */
  name?: string;
  /** 是否显示左上角返回按钮（默认 false）；按下了走 history.back */
  showBack?: boolean;
  /** 返回按钮自定义 onClick；不传则 navigate(-1) */
  onBack?: () => void;
  /** 是否显示主区图片计数（如 "1/3"），默认 false */
  showCounter?: boolean;
  onImageClick?: (index: number) => void;
}

export default function ImageCarousel({
  images,
  height = 240,
  autoplayDelay = 3000,
  name,
  showBack = false,
  onBack,
  showCounter = false,
  onImageClick,
}: ImageCarouselProps) {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const mainSwiperRef = useRef<SwiperType | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);

  if (!images || images.length === 0) {
    return (
      <div
        className="relative bg-gray-100 flex items-center justify-center"
        style={{ height }}
      >
        <i className="ri-image-line text-4xl text-gray-300" />
      </div>
    );
  }

  const handleImageClick = (index: number) => {
    if (onImageClick) {
      onImageClick(index);
    } else {
      setPreviewIndex(index);
      setShowPreview(true);
      // 用户进入大图预览 → 暂停自动播放
      mainSwiperRef.current?.autoplay?.stop();
    }
  };

  // 关闭大图预览后恢复自动播放
  const closePreview = () => {
    setShowPreview(false);
    if (autoplayDelay > 0) {
      mainSwiperRef.current?.autoplay?.start();
    }
  };

  return (
    <>
      {/* 主轮播：支持自动播放 + 手动左右滑动 + 指示器 */}
      <div className="relative" style={{ height }}>
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true, bulletClass: "orange-bullet", bulletActiveClass: "orange-bullet-active" }}
          autoplay={
            autoplayDelay > 0
              ? { delay: autoplayDelay, disableOnInteraction: false, stopOnLastSlide: false }
              : false
          }
          loop={images.length > 1}
          spaceBetween={0}
          slidesPerView={1}
          style={{ height }}
          onSwiper={(sw) => (mainSwiperRef.current = sw)}
          onSlideChange={(sw) => setActiveIndex(sw.realIndex)}
          // 鼠标 hover / 触摸时暂停，离开恢复
          onMouseEnter={() => mainSwiperRef.current?.autoplay?.stop()}
          onMouseLeave={() => {
            if (!showPreview && autoplayDelay > 0) {
              mainSwiperRef.current?.autoplay?.start();
            }
          }}
          onTouchStart={(_sw, e) => {
            mainSwiperRef.current?.autoplay?.stop();
            const t = (e as unknown as TouchEvent).touches[0];
            touchStartRef.current = { x: t.clientX, y: t.clientY };
          }}
          onTouchEnd={(_sw, e) => {
            if (!showPreview && autoplayDelay > 0) {
              mainSwiperRef.current?.autoplay?.start();
            }
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (!start) return;
            const te = e as unknown as TouchEvent;
            const t = te.changedTouches[0];
            const dx = t.clientX - start.x;
            // 横向滑动距离较大视为滑动（而非点击），用于抑制随后触发的 click
            swipedRef.current = Math.abs(dx) > 10;
          }}
          className="image-carousel-swiper relative z-0"
        >
          {images.map((img, i) => (
            <SwiperSlide key={i}>
              <img
                src={img}
                alt={name ? `${name} ${i + 1}` : ""}
                className="w-full h-full object-cover object-center cursor-zoom-in"
                draggable={false}
                onClick={() => {
                  if (swipedRef.current) {
                    swipedRef.current = false;
                    return;
                  }
                  handleImageClick(i);
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 左上角返回按钮（可选） */}
        {showBack && (
          <button
            onClick={onBack || (() => navigate(-1))}
            className="absolute left-3 top-3 z-10 w-11 h-11 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(0,0,0,0.4)" }}
          >
            <i className="ri-arrow-left-line text-white" style={{ fontSize: 22 }} />
          </button>
        )}

        {/* 主区图片计数（可选） */}
        {showCounter && (
          <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
            <span
              className="text-xs text-white px-2 py-0.5 rounded-full font-medium"
              style={{ background: "rgba(0,0,0,0.45)" }}
            >
              {activeIndex + 1}/{images.length}
            </span>
          </div>
        )}
      </div>

      {/* 大图预览弹窗（支持缩放和滑动） */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black"
          onClick={closePreview}
        >
          {/* 关闭按钮（避让顶部刘海） */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closePreview();
            }}
            className="absolute right-4 w-11 h-11 flex items-center justify-center rounded-full z-[60]"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-close-line text-white text-xl" />
          </button>

          {/* 图片数量（避让顶部刘海） */}
          <div
            className="absolute left-4 px-3 py-1.5 rounded-full z-[60]"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", background: "rgba(0,0,0,0.5)" }}
          >
            <span className="text-xs text-white font-medium">
              {previewIndex + 1}/{images.length}
            </span>
          </div>

          <Swiper
            zoom={{ maxRatio: 3 }}
            modules={[Zoom]}
            initialSlide={previewIndex}
            spaceBetween={0}
            slidesPerView={1}
            className="h-full w-full preview-swiper"
            onSlideChange={(swiper: SwiperType) => setPreviewIndex(swiper.activeIndex)}
          >
            {images.map((img, i) => (
              <SwiperSlide key={i} zoom>
                <div className="swiper-zoom-container flex items-center justify-center">
                  <img
                    src={img}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                    draggable={false}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* 底部指示器 */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[60] flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewIndex(i);
                  const sw = (document.querySelector(".preview-swiper") as HTMLElement & { swiper?: SwiperType })?.swiper;
                  sw?.slideTo(i);
                }}
                className="w-2 h-2 rounded-full transition-all cursor-pointer"
                style={{
                  background: i === previewIndex ? "#fff" : "rgba(255,255,255,0.4)",
                  width: i === previewIndex ? 18 : 6,
                }}
              />
            ))}
          </div>
        </div>
      )}
      {/* 主轮播分页指示器（小圆圈）样式：活动项橙色 */}
      <style>{`
        .orange-bullet {
          width: 6px;
          height: 6px;
          margin: 0 2px;
          border-radius: 9999px;
          display: inline-block;
          background: rgba(255, 255, 255, 0.5);
          opacity: 1;
          transition: width 0.2s, background 0.2s;
        }
        .orange-bullet-active {
          background: #F97316;
          width: 18px;
        }
      `}</style>
    </>
  );
}
